import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import { RiCropLine, RiDeleteBinLine, RiFileCopyLine, RiFileUploadLine, RiZoomInLine } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { toast } from "react-toastify";
import QuestionImageCropEditor from "../../../../components/admin/QuestionImageCropEditor";
import MainModel from "../../../../components/Models/main-model";
import { getAllChaptersRequest } from "../../../../modules/admin-modules/chapters/Actions";
import { closeBulkImportQuestionsModel } from "../../../../modules/admin-modules/models/Actions";
import {
  bulkCreateQuestionBankRequest,
  splitPdfQuestionsFail,
  splitPdfQuestionsRequest,
} from "../../../../modules/admin-modules/questions-bank/Actions";
import { getAllTagsRequest } from "../../../../modules/admin-modules/tags/Actions";

const difficultyOptions = [
  { value: "1", label: "notes" },
  { value: "2", label: "كتاب المدرسة" },
  { value: "3", label: "hard questions" },
  { value: "4", label: "امتحانات السنين السابقة" },
  { value: "5", label: "Revision" },
];

const makeDraftFromSplit = (questions = []) =>
  questions.map((q, idx) => ({
    key: `${q.page}-${q.index}-${idx}`,
    page: q.page,
    index: q.index,
    image_base64: q.image_base64,
    original_image_base64: q.image_base64,
    mime_type: q.mime_type || "image/jpeg",
    question: `Q${idx + 1}`,
    mark: 1,
    difficulty_level: "1",
    selectedTags: [],
    url: "",
    model_answer_image_base64: null,
    model_answer_mime_type: null,
    model_answer_file_name: "",
  }));

const renumberDraftQuestions = (list) =>
  list.map((item, idx) => ({ ...item, question: `Q${idx + 1}` }));

const imageDataUrl = (item) => `data:${item.mime_type};base64,${item.image_base64}`;

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      const base64 = result.includes(",") ? result.split(",")[1] : result;
      resolve({
        base64,
        mime_type: file.type || "application/octet-stream",
        name: file.name,
      });
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });

const BulkImportQuestions = () => {
  const dispatch = useDispatch();
  const openModal = useSelector((state) => state.model.isOpen44);
  const chapters = useSelector((state) => state.chapters.chapters);
  const tags = useSelector((state) => state.adminTags.tags);
  const isLoading = useSelector((state) => state.questionsBank.isLoading);
  const splitLoading = useSelector((state) => state.questionsBank.splitLoading);
  const splitResult = useSelector((state) => state.questionsBank.splitResult);

  const [selectedFile, setSelectedFile] = useState(null);
  const [pages, setPages] = useState("");
  const [chapterId, setChapterId] = useState("");
  const [draftQuestions, setDraftQuestions] = useState([]);
  const [previewItem, setPreviewItem] = useState(null);
  const [cropItem, setCropItem] = useState(null);

  const colourStyles = {
    control: (styles) => ({
      ...styles,
      width: "100%",
      background: "#f5f5f5",
      color: "#690000",
      borderRadius: "16px",
    }),
    option: (styles) => ({ ...styles, color: "#690000" }),
    menuPortal: (base) => ({ ...base, zIndex: 2000 }),
    menuList: (base) => ({
      ...base,
      maxHeight: "240px",
    }),
  };
  const selectMenuPortalProps = {
    menuPortalTarget: typeof document !== "undefined" ? document.body : null,
    menuPosition: "fixed",
  };

  const chaptersOptions = useMemo(
    () =>
      (chapters || [])
        .filter((c) => Number(c?.active) === 0)
        .map((c) => ({ value: c.id, label: c.name })),
    [chapters]
  );

  const tagsOptions = useMemo(
    () => (tags || []).map((t) => ({ value: t.id, label: t.name })),
    [tags]
  );

  useEffect(() => {
    if (openModal) {
      dispatch(getAllChaptersRequest({ active: 0 }));
      dispatch(getAllTagsRequest());
    }
  }, [openModal, dispatch]);

  useEffect(() => {
    const questions = splitResult?.questions || splitResult?.data?.questions;
    if (questions?.length) {
      setDraftQuestions(makeDraftFromSplit(questions));
    }
  }, [splitResult]);

  const resetForm = () => {
    setSelectedFile(null);
    setPages("");
    setChapterId("");
    setDraftQuestions([]);
    setPreviewItem(null);
    setCropItem(null);
  };

  const closeFunc = () => {
    dispatch(splitPdfQuestionsFail());
    dispatch(closeBulkImportQuestionsModel());
    resetForm();
  };

  const handleSplit = () => {
    if (!selectedFile) {
      toast.error("اختر ملف PDF أو صورة أولاً");
      return;
    }
    const formData = new FormData();
    formData.append("file", selectedFile);
    if (pages.trim()) formData.append("pages", pages.trim());
    dispatch(splitPdfQuestionsRequest(formData));
  };

  const updateDraft = (key, patch) => {
    setDraftQuestions((prev) =>
      prev.map((item) => (item.key === key ? { ...item, ...patch } : item))
    );
  };

  const removeDraft = (key) => {
    setDraftQuestions((prev) => renumberDraftQuestions(prev.filter((item) => item.key !== key)));
    if (previewItem?.key === key) setPreviewItem(null);
    if (cropItem?.key === key) setCropItem(null);
  };

  const duplicateDraft = (key) => {
    setDraftQuestions((prev) => {
      const index = prev.findIndex((item) => item.key === key);
      if (index < 0) return prev;
      const source = prev[index];
      const copy = {
        ...source,
        key: `${source.key}-copy-${Date.now()}`,
        selectedTags: [...(source.selectedTags || [])],
      };
      const next = [...prev];
      next.splice(index + 1, 0, copy);
      return renumberDraftQuestions(next);
    });
  };

  const handleHintUpload = async (key, file) => {
    if (!file) return;
    try {
      const { base64, mime_type, name } = await fileToBase64(file);
      updateDraft(key, {
        model_answer_image_base64: base64,
        model_answer_mime_type: mime_type,
        model_answer_file_name: name,
      });
    } catch (err) {
      toast.error("فشل قراءة صورة الإجابة");
    }
  };

  const clearHint = (key) => {
    updateDraft(key, {
      model_answer_image_base64: null,
      model_answer_mime_type: null,
      model_answer_file_name: "",
    });
  };

  const applyCrop = (key, cropped) => {
    updateDraft(key, {
      image_base64: cropped.image_base64,
      mime_type: cropped.mime_type,
    });
  };

  const resetCrop = (key) => {
    const item = draftQuestions.find((q) => q.key === key);
    if (!item?.original_image_base64) return;
    updateDraft(key, {
      image_base64: item.original_image_base64,
      mime_type: item.mime_type || "image/jpeg",
    });
  };

  const handleImport = () => {
    if (!chapterId) {
      toast.error("اختر حافظة الاسئلة");
      return;
    }
    if (!draftQuestions.length) {
      toast.error("لا توجد أسئلة للاستيراد");
      return;
    }

    const payload = {
      chapter_id: chapterId,
      questions: draftQuestions.map((q) => {
        const item = {
          question: q.question,
          mark: Number(q.mark) || 1,
          difficulty_level: Number(q.difficulty_level) || 1,
          tags: (q.selectedTags || []).map((t) => t.value),
          image_base64: q.image_base64,
          mime_type: q.mime_type,
        };
        if (q.url?.trim()) item.url = q.url.trim();
        if (q.model_answer_image_base64) {
          item.model_answer_image_base64 = q.model_answer_image_base64;
          item.model_answer_mime_type = q.model_answer_mime_type || "image/jpeg";
        }
        return item;
      }),
    };

    dispatch(
      bulkCreateQuestionBankRequest(payload, () => {
        closeFunc();
      })
    );
  };

  const header = (
    <Box>
      <Heading textAlign="center" color="main_1" fontSize="24px">
        استيراد من PDF
      </Heading>
      <Text fontSize="sm" mt={1} color="gray.600" textAlign="center">
        تقسيم الورقة إلى صور أسئلة وإضافتها لبنك الأسئلة
      </Text>
    </Box>
  );

  return (
    <MainModel
      isLoading={isLoading}
      header={header}
      closeFunc={closeFunc}
      openVar={openModal}
      buttonTitle={null}
      size="6xl"
      hideDefaultFooter
    >
      <Stack spacing={6} w="100%" dir="rtl">
        <Box p={4} borderWidth="1px" borderColor="gray.200" borderRadius="16px" bg="#fafafa">
          <Text fontWeight="600" color="main_1" mb={3}>
            الخطوة 1 — رفع الملف والتقسيم
          </Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <VStack align="stretch" spacing={2}>
              <FormLabel m={0} fontSize="14px" fontWeight="600">
                PDF أو صورة
              </FormLabel>
              <Button
                leftIcon={<RiFileUploadLine />}
                bg="main_1"
                color="white"
                borderRadius="12px"
                position="relative"
              >
                {selectedFile ? selectedFile.name : "اختر ملف"}
                <Input
                  type="file"
                  accept="application/pdf,image/*,.pdf"
                  opacity="0"
                  position="absolute"
                  w="100%"
                  h="100%"
                  onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                />
              </Button>
            </VStack>
            <VStack align="stretch" spacing={2}>
              <FormLabel m={0} fontSize="14px" fontWeight="600">
                الصفحات (اختياري، مثال 1-3)
              </FormLabel>
              <Input
                value={pages}
                onChange={(e) => setPages(e.target.value)}
                placeholder="كل الصفحات إذا تركت فارغاً"
                bg="white"
                borderRadius="12px"
              />
            </VStack>
          </SimpleGrid>
          <Button
            mt={4}
            bg="main_1"
            color="white"
            borderRadius="16px"
            fontWeight="700"
            isLoading={splitLoading}
            onClick={handleSplit}
          >
            تقسيم إلى أسئلة
          </Button>
        </Box>

        {draftQuestions.length > 0 ? (
          <>
            <Box
              position="sticky"
              top={0}
              zIndex={2}
              p={4}
              borderWidth="1px"
              borderColor="gray.200"
              borderRadius="16px"
              bg="white"
              boxShadow="sm"
            >
              <Text fontWeight="600" color="main_1" mb={3}>
                الخطوة 2 — حافظة الاسئلة
              </Text>
              <FormControl>
                <FormLabel m={0} mb={2} fontSize="14px" fontWeight="600">
                  حافظة الاسئلة (مطلوب)
                </FormLabel>
                <Select
                  options={chaptersOptions}
                  value={chaptersOptions.find((o) => String(o.value) === String(chapterId)) || null}
                  onChange={(opt) => setChapterId(opt?.value ?? "")}
                  styles={colourStyles}
                  placeholder="اختر حافظة الاسئلة"
                  {...selectMenuPortalProps}
                />
              </FormControl>
              <Text mt={2} fontSize="sm" color="gray.600">
                {draftQuestions.length} سؤال تم اكتشافه
              </Text>
            </Box>

            <Stack spacing={4}>
              {draftQuestions.map((item, idx) => (
                <Box
                  key={item.key}
                  p={4}
                  borderWidth="1px"
                  borderColor="gray.200"
                  borderRadius="16px"
                  bg="#fafafa"
                >
                  <Flex justify="space-between" align="flex-start" gap={4} mb={3}>
                    <Text fontWeight="700" color="main_1">
                      سؤال {idx + 1}
                      {item.page != null ? ` (صفحة ${item.page})` : ""}
                    </Text>
                    <HStack spacing={2}>
                      <Button
                        size="sm"
                        leftIcon={<RiFileCopyLine />}
                        variant="outline"
                        borderColor="main_1"
                        color="main_1"
                        fontWeight="700"
                        onClick={() => duplicateDraft(item.key)}
                      >
                        نسخ
                      </Button>
                      <Button
                        size="sm"
                        leftIcon={<RiDeleteBinLine />}
                        variant="outline"
                        borderColor="red.400"
                        color="red.500"
                        fontWeight="700"
                        onClick={() => removeDraft(item.key)}
                      >
                        حذف
                      </Button>
                    </HStack>
                  </Flex>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <Box
                      borderWidth="1px"
                      borderColor="gray.200"
                      borderRadius="12px"
                      overflow="hidden"
                      bg="white"
                      p={2}
                    >
                      <Box
                        as="button"
                        type="button"
                        onClick={() => setPreviewItem(item)}
                        w="100%"
                        borderRadius="8px"
                        overflow="hidden"
                        cursor="pointer"
                        _hover={{ opacity: 0.92 }}
                      >
                        <Image
                          src={imageDataUrl(item)}
                          alt={`سؤال ${idx + 1}`}
                          maxH="240px"
                          objectFit="contain"
                          mx="auto"
                          display="block"
                        />
                      </Box>
                      <HStack mt={2} spacing={2} flexWrap="wrap">
                        <Button
                          size="sm"
                          leftIcon={<RiZoomInLine />}
                          variant="outline"
                          borderColor="main_1"
                          color="main_1"
                          fontWeight="700"
                          onClick={() => setPreviewItem(item)}
                        >
                          عرض
                        </Button>
                        <Button
                          size="sm"
                          leftIcon={<RiCropLine />}
                          variant="outline"
                          borderColor="main_1"
                          color="main_1"
                          fontWeight="700"
                          onClick={() => setCropItem(item)}
                        >
                          قص
                        </Button>
                        {item.original_image_base64 &&
                        item.image_base64 !== item.original_image_base64 ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            fontWeight="700"
                            onClick={() => resetCrop(item.key)}
                          >
                            إلغاء القص
                          </Button>
                        ) : null}
                      </HStack>
                    </Box>
                    <Stack spacing={3}>
                      <FormControl>
                        <FormLabel m={0} mb={1} fontSize="14px" fontWeight="600">
                          نص السؤال
                        </FormLabel>
                        <Input
                          value={item.question}
                          onChange={(e) => updateDraft(item.key, { question: e.target.value })}
                          bg="white"
                          borderRadius="12px"
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel m={0} mb={1} fontSize="14px" fontWeight="600">
                          درجة السؤال
                        </FormLabel>
                        <Input
                          type="number"
                          min={1}
                          value={item.mark}
                          onChange={(e) => updateDraft(item.key, { mark: e.target.value })}
                          bg="white"
                          borderRadius="12px"
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel m={0} mb={1} fontSize="14px" fontWeight="600">
                          مستوي السؤال
                        </FormLabel>
                        <HStack spacing={4} flexWrap="wrap">
                          {difficultyOptions.map((opt) => (
                            <label key={opt.value}>
                              <input
                                type="radio"
                                name={`difficulty-${item.key}`}
                                value={opt.value}
                                checked={String(item.difficulty_level) === opt.value}
                                onChange={() =>
                                  updateDraft(item.key, { difficulty_level: opt.value })
                                }
                              />{" "}
                              {opt.label}
                            </label>
                          ))}
                        </HStack>
                      </FormControl>
                      <FormControl>
                        <FormLabel m={0} mb={1} fontSize="14px" fontWeight="600">
                          رابط هينت الفيديو (يوتيوب)
                        </FormLabel>
                        <Input
                          type="url"
                          value={item.url || ""}
                          onChange={(e) => updateDraft(item.key, { url: e.target.value })}
                          placeholder="الصق رابط يوتيوب هنا"
                          bg="white"
                          borderRadius="12px"
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel m={0} mb={1} fontSize="14px" fontWeight="600">
                          صورة اجابة السؤال (اختياري)
                        </FormLabel>
                        <HStack spacing={2} flexWrap="wrap">
                          <Button
                            size="sm"
                            bg="main_1"
                            color="white"
                            borderRadius="12px"
                            position="relative"
                            fontWeight="700"
                          >
                            {item.model_answer_file_name || "اختر ملف"}
                            <Input
                              type="file"
                              accept="image/*"
                              opacity="0"
                              position="absolute"
                              w="100%"
                              h="100%"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handleHintUpload(item.key, file);
                                e.target.value = "";
                              }}
                            />
                          </Button>
                          {item.model_answer_file_name ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              fontWeight="700"
                              onClick={() => clearHint(item.key)}
                            >
                              مسح
                            </Button>
                          ) : null}
                        </HStack>
                      </FormControl>
                      <FormControl>
                        <FormLabel m={0} mb={1} fontSize="14px" fontWeight="600">
                          العلامات Tags
                        </FormLabel>
                        <Select
                          options={tagsOptions}
                          isMulti
                          value={item.selectedTags}
                          onChange={(selected) =>
                            updateDraft(item.key, { selectedTags: selected || [] })
                          }
                          styles={colourStyles}
                          placeholder="Tags"
                          {...selectMenuPortalProps}
                        />
                      </FormControl>
                    </Stack>
                  </SimpleGrid>
                </Box>
              ))}
            </Stack>

            <Flex justify="center" gap={4} pt={2} flexDir="column" align="center">
              <Button
                bg="main_1"
                color="white"
                borderRadius="16px"
                px={8}
                minW="200px"
                fontWeight="700"
                isLoading={isLoading}
                onClick={handleImport}
              >
                استيراد {draftQuestions.length} سؤال
              </Button>
              <Button
                variant="outline"
                color="main_1"
                borderRadius="16px"
                minW="200px"
                fontWeight="700"
                onClick={closeFunc}
              >
                الغاء
              </Button>
            </Flex>
          </>
        ) : null}
      </Stack>

      <Modal
        isOpen={Boolean(previewItem)}
        onClose={() => setPreviewItem(null)}
        size="4xl"
        isCentered
      >
        <ModalOverlay bg="blackAlpha.700" />
        <ModalContent bg="white" maxW="min(90vw, 960px)">
          <ModalHeader color="main_1">
            {previewItem?.question || "معاينة السؤال"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6} display="flex" flexDirection="column" alignItems="center" gap={4}>
            {previewItem ? (
              <>
                <Image
                  src={imageDataUrl(previewItem)}
                  alt={previewItem.question}
                  maxH="75vh"
                  maxW="100%"
                  objectFit="contain"
                />
                <Button
                  leftIcon={<RiCropLine />}
                  bg="main_1"
                  color="white"
                  fontWeight="700"
                  onClick={() => {
                    setCropItem(previewItem);
                    setPreviewItem(null);
                  }}
                >
                  قص هذه الصورة
                </Button>
              </>
            ) : null}
          </ModalBody>
        </ModalContent>
      </Modal>

      <QuestionImageCropEditor
        isOpen={Boolean(cropItem)}
        title={cropItem ? `قص — ${cropItem.question}` : "قص صورة السؤال"}
        src={
          cropItem
            ? `data:${cropItem.mime_type};base64,${cropItem.original_image_base64 || cropItem.image_base64}`
            : ""
        }
        mimeType={cropItem?.mime_type || "image/jpeg"}
        onApply={(cropped) => cropItem && applyCrop(cropItem.key, cropped)}
        onClose={() => setCropItem(null)}
      />
    </MainModel>
  );
};

export default BulkImportQuestions;
